// Client-side mirrors of the backend accept/dismiss side effects
// (apply_ai_fields_to_security / DismissAIFieldView), used to patch the
// store in place instead of refetching the whole cap table. SCAL-8959.

type AnySecurity = Record<string, any>;

const refFieldName = (ref: any): string | undefined => ref?.field_name ?? ref?.fieldName;

export const pendingFieldNames = (security: AnySecurity): string[] => {
  const metadata = security?.ai_finding?.field_metadata ?? {};
  return Object.entries(metadata)
    .filter(([, meta]: [string, any]) => meta != null && !meta.is_accepted)
    .map(([fieldName]) => fieldName);
};

// Coercion contract: the AI metadata `value` is written onto the security field
// as-is (no type coercion, unlike the backend's cast_ai_field_value); the parser
// normalizes display and the next full fetch reconciles formatting.
export const applyAcceptToSecurity = (security: AnySecurity, fieldNames: string[]): AnySecurity => {
  const aiFinding = security?.ai_finding;
  if (!aiFinding?.field_metadata) return security;
  const targets = fieldNames.filter(f => {
    const meta = aiFinding.field_metadata[f];
    return meta != null && !meta.is_accepted;
  });
  if (!targets.length) return security;

  const fieldMetadata = { ...aiFinding.field_metadata };
  const next: AnySecurity = { ...security, ai_finding: { ...aiFinding, field_metadata: fieldMetadata } };
  targets.forEach(f => {
    const meta = fieldMetadata[f];
    fieldMetadata[f] = { ...meta, is_accepted: true };
    if (meta.value !== undefined) next[f] = meta.value;
  });
  return next;
};

export const applyDismissToSecurity = (security: AnySecurity, fieldNames: string[]): AnySecurity => {
  const aiFinding = security?.ai_finding;
  if (!aiFinding?.field_metadata) return security;
  const targets = fieldNames.filter(f => aiFinding.field_metadata[f] != null);
  if (!targets.length) return security;

  const fieldMetadata = { ...aiFinding.field_metadata };
  targets.forEach(f => delete fieldMetadata[f]);
  const dropRefs = (refs: any[] | undefined) =>
    Array.isArray(refs) ? refs.filter(r => !targets.includes(refFieldName(r) as string)) : refs;

  return {
    ...security,
    ai_finding: { ...aiFinding, field_metadata: fieldMetadata },
    citation_references: dropRefs(security.citation_references),
    mapping_references: dropRefs(security.mapping_references),
  };
};

export const removeCitationFromSecurities = (securities: AnySecurity[], citationId: number): AnySecurity[] =>
  securities.map(s => {
    const refs = s.citation_references;
    if (!Array.isArray(refs)) return s;
    const next = refs.filter((r: any) => r?.id !== citationId);
    return next.length === refs.length ? s : { ...s, citation_references: next };
  });
